// Generic Firestore CRUD helpers used by all pages and admin
import {
  collection, query, where, orderBy, limit as fsLimit, getDocs, getDoc,
  doc, addDoc, setDoc, updateDoc, deleteDoc, serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase/config";

// ---------- generic ----------
// Simple in-memory + sessionStorage cache to speed up repeated listAll() calls
// on hot navigations (e.g. Home → Catalog → Home).
const _memCache = new Map();
const CACHE_TTL_MS = 60_000; // 1 min — fresh enough for a browsing session
const _cacheKey = (col, opts) => `fs:${col}:${JSON.stringify(opts || {})}`;
const _readCache = (key) => {
  const mem = _memCache.get(key);
  if (mem && Date.now() - mem.t < CACHE_TTL_MS) return mem.v;
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && Date.now() - parsed.t < CACHE_TTL_MS) {
      _memCache.set(key, parsed);
      return parsed.v;
    }
  } catch (_) { /* ignore */ }
  return null;
};
const _writeCache = (key, v) => {
  const entry = { t: Date.now(), v };
  _memCache.set(key, entry);
  try { sessionStorage.setItem(key, JSON.stringify(entry)); } catch (_) { /* quota exceeded */ }
};
export const invalidateFsCache = (col) => {
  const prefix = col ? `fs:${col}:` : "fs:";
  for (const k of Array.from(_memCache.keys())) {
    if (k.startsWith(prefix)) _memCache.delete(k);
  }
  try {
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith(prefix)) sessionStorage.removeItem(k);
    }
  } catch (_) { /* ignore */ }
};

export async function listAll(col, opts = {}) {
  const { sort, dir = "desc", lim, noCache } = opts;
  const key = _cacheKey(col, { sort, dir, lim });
  if (!noCache) {
    const cached = _readCache(key);
    if (cached) return cached;
  }
  const parts = [collection(db, col)];
  if (sort) parts.push(orderBy(sort, dir));
  if (lim) parts.push(fsLimit(lim));
  const snap = await getDocs(query(...parts));
  const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  _writeCache(key, items);
  return items;
}

export async function getById(col, id) {
  const snap = await getDoc(doc(db, col, id));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function getBySlug(col, slug) {
  const snap = await getDocs(query(collection(db, col), where("slug", "==", slug), fsLimit(1)));
  return snap.empty ? null : { id: snap.docs[0].id, ...snap.docs[0].data() };
}

export async function createDoc(col, data, customId) {
  const payload = { ...data, created_at: serverTimestamp(), updated_at: serverTimestamp() };
  if (customId) {
    await setDoc(doc(db, col, customId), payload);
    invalidateFsCache(col);
    return { id: customId, ...payload };
  }
  const ref = await addDoc(collection(db, col), payload);
  invalidateFsCache(col);
  return { id: ref.id, ...payload };
}

export async function updateDocById(col, id, data) {
  await updateDoc(doc(db, col, id), { ...data, updated_at: serverTimestamp() });
  invalidateFsCache(col);
}

export async function removeDoc(col, id) {
  await deleteDoc(doc(db, col, id));
  invalidateFsCache(col);
}

// ---------- domain-specific ----------
export async function listCategoriesTree() {
  const all = await listAll("categories", { sort: "order", dir: "asc" });
  const byParent = {};
  for (const c of all) {
    const p = c.parent_id || "_root";
    (byParent[p] = byParent[p] || []).push(c);
  }
  // attach children recursively
  const attach = (cat) => ({ ...cat, children: (byParent[cat.id] || []).map(attach) });
  return (byParent._root || []).map(attach);
}

export async function listCategoryDescendantIds(rootId) {
  const all = await listAll("categories");
  const result = new Set([rootId]);
  let changed = true;
  while (changed) {
    changed = false;
    for (const c of all) {
      if (result.has(c.parent_id) && !result.has(c.id)) {
        result.add(c.id);
        changed = true;
      }
    }
  }
  return [...result];
}

export async function listProducts({ q, category_id, artist_id, featured, new_arrival, lim } = {}) {
  let categoryIds = null;
  if (category_id) categoryIds = await listCategoryDescendantIds(category_id);

  // we sort client-side to keep query simple (avoids index requirements)
  const all = await listAll("products");
  let items = all;

  if (q) {
    const needle = q.toLowerCase();
    items = items.filter((p) =>
      [p.title, p.description, p.catalog_no].some((v) => (v || "").toLowerCase().includes(needle)),
    );
  }
  if (categoryIds) {
    const set = new Set(categoryIds);
    items = items.filter((p) => {
      if (p.category_id && set.has(p.category_id)) return true;
      if (Array.isArray(p.category_ids) && p.category_ids.some((id) => set.has(id))) return true;
      return false;
    });
  }
  if (artist_id) items = items.filter((p) => p.artist_id === artist_id);
  if (featured !== undefined && featured !== null) items = items.filter((p) => Boolean(p.featured) === featured);
  if (new_arrival !== undefined && new_arrival !== null) items = items.filter((p) => Boolean(p.new_arrival) === new_arrival);

  items.sort((a, b) => {
    const ax = a.created_at?.toMillis ? a.created_at.toMillis() : 0;
    const bx = b.created_at?.toMillis ? b.created_at.toMillis() : 0;
    return bx - ax;
  });

  return lim ? items.slice(0, lim) : items;
}

export async function getProductBySlug(slug) {
  const product = await getBySlug("products", slug);
  if (!product) return null;
  const [artist, category] = await Promise.all([
    product.artist_id ? getById("artists", product.artist_id) : null,
    product.category_id ? getById("categories", product.category_id) : null,
  ]);
  const all = await listAll("products");
  const related = all.filter((p) => {
    if (p.id === product.id) return false;
    if (product.category_id && p.category_id === product.category_id) return true;
    const aIds = Array.isArray(product.category_ids) ? product.category_ids : [];
    const bIds = Array.isArray(p.category_ids) ? p.category_ids : [];
    if (aIds.length && bIds.length && aIds.some((id) => bIds.includes(id))) return true;
    if (aIds.length && p.category_id && aIds.includes(p.category_id)) return true;
    if (bIds.length && product.category_id && bIds.includes(product.category_id)) return true;
    return false;
  }).slice(0, 4);
  return { product, artist, category, related };
}

export async function getArtistBySlug(slug) {
  // Eyni slug-a malik birdən çox sənətçi ola bilər (data düzgün doldurulmayıb).
  // Buna görə əvvəlcə ID üzrə tap, tapılmasa slug üzrə axtar.
  let artist = await getById("artists", slug);
  if (!artist) artist = await getBySlug("artists", slug);
  if (!artist) return null;
  const products = await listProducts({ artist_id: artist.id });
  return { artist, products };
}

export async function getCategoryBySlug(slug) {
  const category = await getBySlug("categories", slug);
  if (!category) return null;
  const products = await listProducts({ category_id: category.id });
  return { category, products };
}

// ---------- site content ----------
export async function getContent(key) {
  const snap = await getDoc(doc(db, "content", key));
  return snap.exists() ? snap.data() : null;
}
export async function setContent(key, data) {
  await setDoc(doc(db, "content", key), { ...data, updated_at: serverTimestamp() }, { merge: true });
}

// ---------- Epoint.az payment provider config ----------
// Two-doc strategy:
//   • settings/epoint  — admin-only writable (full secrets)
//   • content/epoint_public — publicly readable mirror (used by the
//     checkout UI to render the payment card for all signed-in customers).
//     content/* rules are already open for read in the site CMS, so we
//     write the full config there too — this matches the existing
//     architecture where the client forwards the private_key to the
//     serverless function anyway.
export async function getEpointConfig() {
  // Try the admin/owner-readable settings doc first (most up-to-date),
  // then fall back to the public mirror so regular signed-in customers
  // can still see and complete checkout.
  try {
    const snap = await getDoc(doc(db, "settings", "epoint"));
    if (snap.exists()) return snap.data();
  } catch (_e) {
    /* permission denied — fall through to public mirror */
  }
  try {
    const snap = await getDoc(doc(db, "content", "epoint_public"));
    if (snap.exists()) return snap.data();
  } catch (_e) {
    /* ignore */
  }
  return null;
}
export async function saveEpointConfig(data) {
  const payload = { ...data, updated_at: serverTimestamp() };
  // Primary (admin-only) document — source of truth.
  await setDoc(doc(db, "settings", "epoint"), payload, { merge: true });
  // Public mirror so non-admin customers can also load the config and
  // complete checkout. Writes are still gated by the admin role on the
  // content/* collection.
  try {
    await setDoc(doc(db, "content", "epoint_public"), payload, { merge: true });
  } catch (_e) {
    /* non-fatal — primary save succeeded */
  }
}
